/**
 * VIP Tiers API client — fetches tiered player data from the backend.
 */
import type { ApiClient, ApiResult } from '../api/client.ts';

// ─── Types ───────────────────────────────────────────────────────────────────

export type TierName = 'La Corona' | 'Diamante' | 'Platino' | 'Oro' | 'Plata' | 'Bronce';

export interface VipPlayer {
  readonly globalId: string;
  readonly userId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly status: string;
  readonly rgLocked: boolean;
  readonly registrationDate: string;
  readonly lastLogin: string;
  readonly firstDepositDate: string;
  readonly lastDepositDate: string;
  readonly daysSinceDeposit: number;
  readonly periodDepositCount: number;
  readonly periodDepositAmount: number;
  readonly periodDepositingDays: number;
  readonly cashStakes: number;
  readonly totalStakes: number;
  readonly totalReturns: number;
  readonly margin: number;
  readonly numberOfBets: number;
  readonly averageBet: number;
  readonly wagerCycle: number;
  readonly totalWithdrawals: number;
  readonly cashHold: number;
  readonly holdPercent: number;
  readonly netCashContribution: number;
  readonly casinoCashStakes: number;
  readonly sportsCashStakes: number;
  readonly tier: TierName;
  readonly nextTier: TierName | null;
  readonly amountToNextTier: number | null;
}

export interface TierSummary {
  readonly tier: TierName;
  readonly playerCount: number;
  readonly totalDeposits: number;
  readonly totalNCC: number;
  readonly avgDeposit: number;
  readonly avgNCC: number;
  readonly percentOfBase: number;
  readonly percentOfDeposits: number;
}

export interface VipTiersResponse {
  readonly totalPlayers: number;
  readonly totalDeposits: number;
  readonly summaries: readonly TierSummary[];
  readonly players: readonly VipPlayer[];
}

// ─── API ─────────────────────────────────────────────────────────────────────

const passthrough = { safeParse: (v: unknown) => ({ success: true as const, data: v }) } as never;

export interface VipTiersApi {
  readonly getTiers: (tiers?: readonly TierName[]) => Promise<ApiResult<VipTiersResponse>>;
}

export const createVipTiersApi = (client: ApiClient): VipTiersApi => ({
  // Returns the `ApiResult` rather than collapsing a failure into a thrown `Error`, so the page can
  // tell the Snowflake-reconnect case (a 503) apart from any other failure — the same shape the
  // owner report client uses. The success body is unchanged.
  getTiers: async (tiers) => {
    const tierParam = tiers?.join(',') ?? 'La Corona,Diamante,Platino,Oro';
    const result = await client.get(
      `/api/admin/analytics/vip-tiers?tier=${encodeURIComponent(tierParam)}`,
      passthrough,
    );
    return result as ApiResult<VipTiersResponse>;
  },
});
